// src/services/offlineQueue.ts
var PORTAL_OFFLINE_QUEUE_KEY = "caro.portal.offline_queue";
function createOfflineQueue(options = {}) {
  const STORAGE_KEY = options.storageKey ?? PORTAL_OFFLINE_QUEUE_KEY;
  const autoReplay = options.autoReplay !== false;
  const load = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };
  const save = (list) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
    }
  };
  const listeners = /* @__PURE__ */ new Set();
  let entries = load();
  let replayInFlight = false;
  let replayClient = null;
  const notify = () => {
    listeners.forEach((fn) => fn());
  };
  const genId = () => `oq_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  const queue = {
    subscribe(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    getSnapshot() {
      return entries;
    },
    pendingCount() {
      return entries.filter((e) => e.status !== "failed").length;
    },
    bindClient(client) {
      replayClient = client;
    },
    enqueue({ method, url, data, config, description }) {
      const entry = {
        id: genId(),
        method: String(method || "post").toLowerCase(),
        url,
        data,
        config: config ? { headers: config.headers, params: config.params } : void 0,
        description: description || `${String(method).toUpperCase()} ${url}`,
        enqueuedAt: Date.now(),
        retryCount: 0,
        status: "pending",
        error: null
      };
      entries = [...entries, entry];
      save(entries);
      notify();
      return entry.id;
    },
    remove(id) {
      entries = entries.filter((e) => e.id !== id);
      save(entries);
      notify();
    },
    clearFailed() {
      entries = entries.filter((e) => e.status !== "failed");
      save(entries);
      notify();
    },
    async replay() {
      if (replayInFlight) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      if (!replayClient) return;
      replayInFlight = true;
      try {
        const pending = entries.filter((e) => e.status === "pending");
        for (const entry of pending) {
          try {
            await replayClient.request({
              method: entry.method,
              url: entry.url,
              data: entry.data,
              headers: entry.config?.headers,
              params: entry.config?.params,
              // Prevent the consumer's own interceptor from re-queueing this if
              // it fails for a non-network reason.
              __offlineReplay: true
            });
            entries = entries.filter((e) => e.id !== entry.id);
          } catch (err) {
            const e = err;
            const isNetworkErr = !e?.response;
            if (isNetworkErr) {
              save(entries);
              notify();
              return;
            }
            entries = entries.map(
              (x) => x.id === entry.id ? {
                ...x,
                status: "failed",
                error: e?.response?.data?.error || e?.message || "Request failed",
                retryCount: x.retryCount + 1
              } : x
            );
          }
          save(entries);
          notify();
        }
      } finally {
        replayInFlight = false;
      }
    }
  };
  if (autoReplay && typeof window !== "undefined") {
    window.addEventListener("online", () => {
      void queue.replay();
    });
    setTimeout(() => {
      if (navigator.onLine && entries.some((e) => e.status === "pending")) {
        void queue.replay();
      }
    }, 2e3);
  }
  return queue;
}
var offlineQueue = createOfflineQueue();

export { PORTAL_OFFLINE_QUEUE_KEY, createOfflineQueue, offlineQueue };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map